/**
 * ModuleRoute Component
 *
 * Route wrapper that guards routes by module enablement.
 * Redirects to dashboard if module is not enabled.
 */

import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useModuleEnabled } from '../hooks/useModuleEnabled';
import {
  selectModulesError,
  selectModulesLastFetched,
  selectModuleTierMin,
} from '../../app/slices/enabledModulesSlice';
import {
  selectSubscriptionTier,
  selectSubscriptionLoaded,
  selectSubscriptionError,
} from '../../app/slices/subscriptionSlice';
import { tierMeets } from '../lib/subscriptionTiers';
import TierRequiredNotice from './TierRequiredNotice';

/**
 * Wrapper component for routes that require a specific module to be enabled
 *
 * @param {string} moduleKey - The module identifier (e.g., 'risk', 'audit')
 * @param {React.ReactNode} element - The route element to render if module is enabled
 * @returns {React.ReactNode} - The element or redirect to dashboard
 *
 * @example
 * <Route
 *   path="/audit-management/*"
 *   element={
 *     <ModuleRoute moduleKey="audit">
 *       <AuditManagementDashboard />
 *     </ModuleRoute>
 *   }
 * />
 */
export function ModuleRoute({ moduleKey, element, children, fallback = null }) {
  const isEnabled = useModuleEnabled(moduleKey);
  const modulesError = useSelector(selectModulesError);
  const modulesFetchedAtLeastOnce = Boolean(useSelector(selectModulesLastFetched));
  const tierMin = useSelector(selectModuleTierMin(moduleKey));
  const tier = useSelector(selectSubscriptionTier);
  const tierLoaded = useSelector(selectSubscriptionLoaded);
  const tierError = useSelector(selectSubscriptionError);

  // Wait only while a lookup is genuinely in flight or has not started, so a page the plan
  // forbids is never flashed. A failed lookup must NOT block the app: the backend enforces
  // module access and tier (RequiresTier), and a 403 there is a clearer answer than a blanket
  // "unavailable" wall over every module because one auxiliary endpoint hiccuped.
  if (!tierLoaded && !tierError) {
    return (
      <div role="status" className="neumorphic-card p-6 text-sm theme-text-secondary text-center">
        Checking subscription…
      </div>
    );
  }

  if (!modulesFetchedAtLeastOnce) {
    if (modulesError) {
      // Module list could not be loaded, so enablement is unknown. Do not consult `isEnabled`
      // (false for everything here): that would redirect every page to the dashboard.
      return children || element || null;
    }
    return (
      <div role="status" className="neumorphic-card p-6 text-sm theme-text-secondary text-center">
        Loading modules…
      </div>
    );
  }

  if (!isEnabled) {
    return fallback || <Navigate to="/dashboard" replace />;
  }

  // PLT-06: enabled but the plan is below the module's minimum tier. Say so up front
  // instead of mounting a page whose every request will 403.
  // `tier` defaults to 'basic' until the subscription loads; only trust it once it has.
  // After a failed lookup the tier is unknown, so fail open and let the API's 403 decide.
  if (!tierMeets(tierLoaded ? tier : null, tierMin)) {
    return <TierRequiredNotice moduleName={moduleKey.replace(/_/g, ' ')} requiredTier={tierMin} />;
  }

  return children || element || null;
}

ModuleRoute.propTypes = {
  moduleKey: PropTypes.string.isRequired,
  element: PropTypes.node,
  children: PropTypes.node,
  fallback: PropTypes.node,
};

/**
 * Conditional wrapper that only renders if module is enabled
 *
 * @param {string} moduleKey - The module identifier
 * @param {React.ReactNode} children - Content to render if enabled
 * @param {React.ReactNode} fallback - Content to render if not enabled
 * @returns {React.ReactNode}
 *
 * @example
 * <ModuleContent moduleKey="kri">
 *   <KRIDashboard />
 *   <p>KRI module is not enabled for your organization</p>
 * </ModuleContent>
 */
export function ModuleContent({ moduleKey, children, fallback = null }) {
  const isEnabled = useModuleEnabled(moduleKey);

  if (!isEnabled) {
    return fallback || null;
  }

  // Handle array of children (children, fallback pattern)
  if (Array.isArray(children) && children.length > 1) {
    return children[0]; // First child is main content
  }

  return children;
}

ModuleContent.propTypes = {
  moduleKey: PropTypes.string.isRequired,
  children: PropTypes.node,
  fallback: PropTypes.node,
};

/**
 * Error boundary component for protected components that require a module
 *
 * @param {string} moduleKey - The module identifier
 * @param {React.ReactNode} children - Content to render if enabled
 * @param {string} moduleName - Display name for error messages
 * @returns {React.ReactNode}
 *
 * @example
 * <RequireModule moduleKey="audit" moduleName="Audit Management">
 *   <AuditPanel />
 * </RequireModule>
 */
export function RequireModule({ moduleKey, children, moduleName = null }) {
  const isEnabled = useModuleEnabled(moduleKey);

  if (!isEnabled) {
    const displayName = moduleName || moduleKey;
    return (
      <div className="p-6 bg-amber-50 border border-amber-300 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="text-amber-600 mt-0.5">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-amber-900">Module Not Enabled</h3>
            <p className="text-sm text-amber-800 mt-1">
              The <strong>{displayName}</strong> module is not enabled for your organization.
              Contact your administrator to enable it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

RequireModule.propTypes = {
  moduleKey: PropTypes.string.isRequired,
  children: PropTypes.node,
  moduleName: PropTypes.string,
};
